import { Injectable } from '@angular/core'
import { DepartmentService } from '@ftr/api-core'
import { CourtroomRealTimeSttConfigurationService, ProducersService, RegionalApiClientFactory } from '@ftr/api-regional'
import { PublicConfiguration } from '@ftr/contracts/api/configuration'
import { Department } from '@ftr/contracts/api/department'
import {
  AudioDevice,
  DesiredProducerConfiguration,
  PostConfigureProducerBody,
  ProducerDetail,
} from '@ftr/contracts/regional-api'
import { LocationStatus } from '@ftr/contracts/type/court'
import { Uuid } from '@ftr/contracts/type/shared'
import { CoreConfigurationService } from '@ftr/data-court-system'
import { ChildLocation, CourtroomService } from '@ftr/data-location'
import { ApiResult, ArrayUtils, MapUtils, createAndAssign, mapData, switchMapData } from '@ftr/foundation'
import { CourtroomRealTimeSttConfiguration } from '@ftr/regional-api-contracts'
import { ZoneId } from '@js-joda/core'
import { lt } from 'semver/preload'

export interface ProducerDetailWithLocation extends Omit<ProducerDetail, 'timezone'> {
  location: ChildLocation | undefined
  courtroomName: string
  courthouseName: string
  isCourtroomArchived: boolean
  timezone: ZoneId
  courthouseTimezone: ZoneId
  /**
   * Fields for the detailed recorder view only, not list.
   */
  departmentName: string | undefined
  courtroomSttConfiguration: CourtroomRealTimeSttConfiguration | undefined
}

/**
 * This service extends ProducersService to add other data like locations and STT settings by combining other APIs.
 * Ideally it would use composition instead of inheritance but I've changed enough in one PR.
 */
@Injectable({
  providedIn: 'root',
})
export class ProducersAugmentedService extends ProducersService {
  constructor(
    private readonly courtroomService: CourtroomService,
    private readonly configurationService: CoreConfigurationService,
    private readonly departmentService: DepartmentService,
    private readonly courtroomRealTimeSttConfigurationService: CourtroomRealTimeSttConfigurationService,
    regionalApiClientFactory: RegionalApiClientFactory,
  ) {
    super(regionalApiClientFactory)
  }

  list(courtSystemId: Uuid, locationId?: Uuid): ApiResult<ProducerDetailWithLocation[]> {
    return this.listProducerDetail(courtSystemId, locationId).pipe(
      switchMapData(details => this.addLocationInfoToProducerDetails(courtSystemId, details)),
    )
  }

  get(courtSystemId: Uuid, producerId: Uuid): ApiResult<ProducerDetailWithLocation> {
    return this.getProducerDetail(courtSystemId, producerId).pipe(
      switchMapData(details =>
        ApiResult.combine([
          ApiResult.success(details),
          details.locationId
            ? this.courtroomService.findById(courtSystemId, details.locationId)
            : ApiResult.success(undefined),
          details.locationId
            ? this.courtroomRealTimeSttConfigurationService.get({ courtSystemId, locationId: details.locationId })
            : ApiResult.success(undefined),
          this.configurationService.getByCourtSystem(courtSystemId),
          this.getDepartmentInformation(details),
        ]),
      ),
      mapData(([detail, courtroom, courtroomSttConfiguration, configuration, department]) =>
        this.addLocationInfoToProducerDetail(courtroom, detail, configuration, department, courtroomSttConfiguration),
      ),
    )
  }

  transformOutputAudioDeviceConfig(configuration: DesiredProducerConfiguration): AudioDevice | undefined {
    if (configuration.outputAudioDevice?.name !== 'No Output Device') {
      return configuration.outputAudioDevice ?? undefined
    }

    return undefined
  }

  configure(courtSystemId: Uuid, producerId: Uuid, configuration: DesiredProducerConfiguration): ApiResult<unknown> {
    if (!configuration.audioDevice || !configuration.locationId) {
      return ApiResult.failure(new Error('Must include both audio device and location id when configuring Recorders'))
    }

    const body: PostConfigureProducerBody = {
      selectedAudioDevice: configuration.audioDevice,
      selectedOutputAudioDevice: this.transformOutputAudioDeviceConfig(configuration),
      selectedCourtroom: configuration.locationId,
      selectedVideoDevices: configuration.videoDevices ?? undefined,
      advancedConfiguration: configuration.advancedConfiguration ?? undefined,
      isStreamEnabled: configuration.enabled,
      mode: configuration.mode,
    }

    return this.postConfiguration(courtSystemId, producerId, body)
  }

  isUpgradeAvailableForProducers(producers: ProducerDetail[], expectedVersion: string): boolean {
    return producers.some(producer => lt(producer.version, expectedVersion))
  }

  private getCourtroomMap(courtSystemId: Uuid, producers: ProducerDetail[]): ApiResult<Map<Uuid, ChildLocation>> {
    return this.courtroomService
      .findAll(courtSystemId, ArrayUtils.distinct(ArrayUtils.collect(producers, x => x.locationId)))
      .pipe(mapData(locations => MapUtils.buildFromValues(locations, l => l.id)))
  }

  // Note: The list view does not currently have department names
  private addLocationInfoToProducerDetails(
    courtSystemId: Uuid,
    details: ProducerDetail[],
  ): ApiResult<ProducerDetailWithLocation[]> {
    return ApiResult.combine([
      this.configurationService.getByCourtSystem(courtSystemId),
      this.getCourtroomMap(courtSystemId, details),
    ]).pipe(
      mapData(([configuration, courtroomsById]) =>
        details.map(d => {
          const location = this.getLocationFromMap(courtroomsById, d)
          return this.addLocationInfoToProducerDetail(location, d, configuration, undefined, undefined)
        }),
      ),
    )
  }

  private getDepartmentInformation(details: ProducerDetail): ApiResult<Department | undefined> {
    const departmentId = details.weeklyStreamLocationConfiguration?.monday?.departmentId
    return departmentId ? this.departmentService.get(departmentId) : ApiResult.success(undefined)
  }

  private getLocationFromMap(
    courtroomsById: Map<Uuid, ChildLocation>,
    producer: ProducerDetail,
  ): ChildLocation | undefined {
    if (producer.locationId) {
      return courtroomsById.get(producer.locationId)
    }
    return
  }

  private addLocationInfoToProducerDetail(
    courtroom: ChildLocation | undefined,
    producerDetail: ProducerDetail,
    configuration: PublicConfiguration,
    department: Department | undefined,
    courtroomRealTimeSttConfiguration?: CourtroomRealTimeSttConfiguration,
  ): ProducerDetailWithLocation {
    const courthouseTimezone = courtroom?.parent.timeZoneId || configuration.timeZoneId
    const timezone = producerDetail.timezone || courthouseTimezone

    if (!producerDetail.locationId) {
      return {
        ...producerDetail,
        location: undefined,
        courtroomSttConfiguration: undefined,
        courtroomName: 'Unconfigured',
        courthouseName: 'Unconfigured',
        departmentName: department?.name,
        isCourtroomArchived: false,
        timezone,
        courthouseTimezone,
      }
    }

    return {
      ...producerDetail,
      location: courtroom,
      courtroomSttConfiguration: courtroomRealTimeSttConfiguration,
      courtroomName: courtroom?.name || 'Unknown Courtroom',
      courthouseName: courtroom?.parent?.name || 'Unknown Courthouse',
      departmentName: department?.name,
      isCourtroomArchived:
        courtroom?.parent?.status === LocationStatus.Archived || courtroom?.status === LocationStatus.Archived,
      timezone,
      courthouseTimezone,
    }
  }

  setEnabled(courtSystemId: Uuid, producerId: Uuid, enabled: boolean): ApiResult<void> {
    return this.getProducerDetail(courtSystemId, producerId).pipe(
      switchMapData(p => {
        if (!p.configuredAudioDevice || !p.locationId) {
          return ApiResult.failure(new Error('Cannot disable a new recorder'))
        }
        return this.postConfiguration(courtSystemId, producerId, convertToPostConfigureProducerBody(p, enabled))
      }),
      mapData(() => undefined),
    )
  }
}

export function convertToPostConfigureProducerBody(
  producer: ProducerDetail,
  enabled?: boolean,
): PostConfigureProducerBody {
  return createAndAssign(PostConfigureProducerBody, {
    selectedAudioDevice: producer.configuredAudioDevice,
    selectedOutputAudioDevice: producer.configuredOutputAudioDevice,
    selectedCourtroom: producer.locationId,
    isStreamEnabled: enabled === undefined ? producer.enabled : enabled,
    selectedVideoDevices: producer.configuredVideoDevices,
    mode: producer.mode,
  })
}
